import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { Box, Skeleton } from '@factorialco/deprecated-design-system'
import { OneDataCollection, OneEmptyState } from '@factorialco/f0-react/dist/experimental'
import { JobCatalogPeopleDataCollectionEmployeeFragment } from 'generated/resources/graphql'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import buildUrl from 'modules/core/lib/buildUrl'
import { stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { JobCatalogPeopleCharts } from 'modules/job_catalog/components/JobCatalogPeopleCharts'
import { getChildTypeText, normalizeNodeType } from 'modules/job_catalog/lib/nodesUtils'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import {
  isPeopleSourceReady,
  shouldShowPeopleSourceFallback,
  useJobCatalogPeople,
  useJobCatalogPeopleSource,
} from './useJobCatalogPeople'
import { usePeopleInsights } from './usePeopleInsights'

type ContractStatusType =
  | 'ongoing'
  | 'ended'
  | 'upcoming'
  | 'in_activity_period'
  | 'in_inactivity_period'

const isRecord = (obj: unknown): obj is Record<string, unknown> => {
  return typeof obj === 'object' && obj !== null
}

const readContractFlags = (
  contract: unknown
): {
  isDiscontinuous: boolean | undefined
  isActive: boolean | undefined
} => {
  let isDiscontinuous: boolean | undefined = undefined
  let isActive: boolean | undefined = undefined

  if (isRecord(contract)) {
    const rawIsDiscontinuous = contract['isDiscontinuous']
    if (typeof rawIsDiscontinuous === 'boolean') {
      isDiscontinuous = rawIsDiscontinuous
    }

    const statusUnknown = contract['status']
    if (isRecord(statusUnknown)) {
      const rawIsActive = statusUnknown['isActive']
      if (typeof rawIsActive === 'boolean') {
        isActive = rawIsActive
      }
    }
  }

  return { isDiscontinuous, isActive }
}

export const getContractStatus = (
  referenceContract:
    | {
        startsOn?: string | null
        endsOn?: string | null
        contract?: unknown | null
        isDiscontinuous?: unknown
        contractStatusIsActive?: unknown
      }
    | null
    | undefined
): ContractStatusType => {
  const today = new Date()

  if (!referenceContract) return 'ongoing'

  const { startsOn, endsOn, contract } = referenceContract

  const flags = readContractFlags(contract)
  const fallbackIsDiscontinuous =
    typeof referenceContract.isDiscontinuous === 'boolean'
      ? referenceContract.isDiscontinuous
      : undefined
  const isDiscontinuous =
    contract != null ? (flags.isDiscontinuous ?? fallbackIsDiscontinuous) : fallbackIsDiscontinuous

  if (isDiscontinuous) {
    const fallbackIsActive =
      typeof referenceContract.contractStatusIsActive === 'boolean'
        ? referenceContract.contractStatusIsActive
        : undefined
    const isActive = contract != null ? (flags.isActive ?? fallbackIsActive) : fallbackIsActive

    if (isActive === true) return 'in_activity_period'
    if (isActive === false) return 'in_inactivity_period'
  }

  if (typeof endsOn === 'string' && stringToDate(endsOn, 'backend').isBefore(today, 'day'))
    return 'ended'
  if (typeof startsOn === 'string' && stringToDate(startsOn, 'backend').isAfter(today, 'day'))
    return 'upcoming'

  return 'ongoing'
}

export const isEmployeeTerminated = (terminatedOn: string | null | undefined) => {
  return (
    typeof terminatedOn === 'string' &&
    stringToDate(terminatedOn, 'backend').isBefore(new Date(), 'day')
  )
}

const contractStatusVariant = (status: ContractStatusType) => {
  switch (status) {
    case 'ongoing':
    case 'in_activity_period':
      return 'positive'
    case 'ended':
      return 'critical'
    case 'upcoming':
      return 'warning'
    case 'in_inactivity_period':
      return 'neutral'
    default:
      return 'neutral'
  }
}

const getContractStatusLabel = (status: ContractStatusType) => {
  switch (status) {
    case 'ongoing':
      return i18n.t('job_catalog.nodes.people.contract_status_ongoing')
    case 'ended':
      return i18n.t('job_catalog.nodes.people.contract_status_ended')
    case 'upcoming':
      return i18n.t('job_catalog.nodes.people.contract_status_upcoming')
    case 'in_activity_period':
      return i18n.t('employees.employees_list.contract_status.in_activity_period')
    case 'in_inactivity_period':
      return i18n.t('employees.employees_list.contract_status.in_inactivity_period')
    default:
      return i18n.t('job_catalog.nodes.people.contract_status_ongoing')
  }
}

const PeopleCollection = ({ id, levelUuids }: { id: string; levelUuids: string[] }) => {
  const source = useJobCatalogPeopleSource(id, levelUuids)
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  return (
    <OneDataCollection
      source={source}
      emptyStates={{
        'no-data': {
          emoji: '🕵️',
          title: i18n.t('job_catalog.nodes.empty_state.people.title'),
          description: i18n.t('job_catalog.nodes.empty_state.people.description'),
          actions: [
            {
              label: i18n.t('job_catalog.nodes.empty_state.people.actions.navigate'),
              onClick: () => navigate(navigation.employees.fullPath()),
              variant: 'outline',
            },
          ],
        },
      }}
      visualizations={[
        {
          type: 'table',
          options: {
            columns: [
              {
                label: i18n.t('job_catalog.nodes.people.employee_name'),
                render: (employee: JobCatalogPeopleDataCollectionEmployeeFragment) => ({
                  type: 'person' as const,
                  value: {
                    firstName: employee.fullName,
                    lastName: '',
                    src: buildUrl(employee.access?.avatar),
                  },
                }),
              },
              {
                label: i18n.t('job_catalog.nodes.people.role'),
                render: (employee: JobCatalogPeopleDataCollectionEmployeeFragment) => ({
                  type: 'tag',
                  value: {
                    label: employee.referenceContract?.jobCatalogTreeLevel?.ancestor?.name ?? '-',
                  },
                }),
              },
              {
                label: i18n.t('job_catalog.nodes.people.level'),
                render: (employee: JobCatalogPeopleDataCollectionEmployeeFragment) => ({
                  type: 'tag',
                  value: {
                    label: employee.referenceContract?.jobCatalogTreeLevel?.name ?? '-',
                  },
                }),
              },
              {
                label: i18n.t('job_catalog.nodes.people.status'),
                render: (employee: JobCatalogPeopleDataCollectionEmployeeFragment) => {
                  const label = isEmployeeTerminated(employee.terminatedOn)
                    ? i18n.t('job_catalog.nodes.people.terminated')
                    : i18n.t('job_catalog.nodes.people.active')
                  return {
                    type: 'tag',
                    value: { label },
                  }
                },
              },
              {
                label: i18n.t('job_catalog.nodes.people.contract_status'),
                render: (employee: JobCatalogPeopleDataCollectionEmployeeFragment) => {
                  const contractStatus = getContractStatus(employee.referenceContract)
                  return {
                    type: 'status',
                    value: {
                      label: getContractStatusLabel(contractStatus),
                      status: contractStatusVariant(contractStatus),
                    },
                  }
                },
              },
            ],
            frozenColumns: 1,
          },
        },
      ]}
    />
  )
}

const PeopleSourceFallback = ({ hasError }: { hasError: boolean }) => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  return (
    <Box paddingY="s20" alignItems="center" width="full" minHeight="s600">
      <OneEmptyState
        emoji={hasError ? '⚠️' : '🕵️'}
        title={
          hasError
            ? i18n.t('job_catalog.nodes.empty_state.people.error.title')
            : i18n.t('job_catalog.nodes.empty_state.people.title')
        }
        description={
          hasError
            ? i18n.t('job_catalog.nodes.empty_state.people.error.description')
            : i18n.t('job_catalog.nodes.empty_state.people.description')
        }
        variant={hasError ? 'critical' : 'default'}
        actions={[
          {
            label: i18n.t('job_catalog.nodes.empty_state.people.actions.navigate'),
            onClick: () => navigate(navigation.employees.fullPath()),
            variant: 'outline',
          },
        ]}
      />
    </Box>
  )
}

const PeoplePage = () => {
  const { rootId, familyId, functionId, roleId, levelId } = useParams()
  const id = levelId || roleId || functionId || familyId || rootId || ''
  const { isLevel, levelUuids, loadingLevels, levelsError } = useJobCatalogPeople(id)
  const { data: insights, loading: insightsLoading } = usePeopleInsights(id)
  const isReady = isPeopleSourceReady(isLevel, levelUuids)
  const showPeopleSourceFallback = shouldShowPeopleSourceFallback({ isReady, loadingLevels })

  useEffect(() => {
    if (!id) return
    track(trackingEvents.jobCatalog.navigateNodeSubmit, {
      event_category: 'Organizations',
      node_type: normalizeNodeType(id),
      node_id: id,
      tab: 'people',
    })
  }, [id])
  const groupedText = getChildTypeText(id).toLowerCase()

  return (
    <ResponsivePage.Body variant="full">
      {(insightsLoading || (insights && insights.totalCount > 0)) && (
        <Box paddingY="s20">
          <JobCatalogPeopleCharts
            insights={insights}
            loading={insightsLoading}
            groupedBy={groupedText}
            isLevel={isLevel}
          />
        </Box>
      )}
      {!isReady && loadingLevels && (
        <Box paddingY="s20" height="s600">
          <Skeleton height="full" width="full" isLoading />
        </Box>
      )}
      {showPeopleSourceFallback && <PeopleSourceFallback hasError={levelsError != null} />}
      {isReady && <PeopleCollection id={id} levelUuids={levelUuids} />}
    </ResponsivePage.Body>
  )
}

export default PeoplePage
